import { useMemo } from "react";
import { LineChartEduExpensesMain } from "./EduLineChart";
import ViewTableExpenses from "./ViewTableExpenses";
import { STYLE_PARENT_TABLE_OVERSCROLL } from "../../../utils/useStyle";
import { MTButton } from "../../../components/button/MTButton";
import * as XLSX from "xlsx";
import _ from "lodash";
import { key_education_rank } from "../functions/options";
import { useUserStore } from "../../../_store/userStore";

const ViewExpensesEDU = ({ localEducationData }) => {
  const userData = useUserStore((state) => state.userData);

  const dataExpenses = useMemo(() => {
    const expenses_items = localEducationData?.education_items?.map(
      (education_item) => {
        return education_item.sum_under_education_accumulate;
      }
    );
    return expenses_items;
  }, [localEducationData]);

  const dataLabels = useMemo(() => {
    const expenses_items = localEducationData?.education_items?.map(
      (education_item) => {
        return education_item?.education?.label;
      }
    );
    return expenses_items;
  }, [localEducationData]);

  const heightTable = localEducationData?.education_items?.length * 50 + 260;
  const height = `${heightTable}px`;

  const checkHeight = (height) => {
    let value = heightTable;
    // console.log(value);

    if (value > 600) {
      return 600;
    } else {
      return value;
    }
  };

  const exportToExcel = () => {
    // Get dynamic headers from first item
    const firstItem = localEducationData?.education_items?.[0];
    
    // Calculate how many invest columns we have
    const investColumnsCount = firstItem?.invest_keys?.length || 0;

    // Create worksheet data array
    const worksheetData = [];

    // Row 1: First layer header (group headers)
    const headerRow1 = [""];
    headerRow1.push("ค่าใช้จ่ายเพื่อการศึกษาและค่าใช้จ่ายในชีวิตประจำวัน");
    headerRow1.push(""); // Empty for merge
    
    // Add investment group header
    if (investColumnsCount > 0) {
      headerRow1.push("ออมเงิน / ลงทุน ดอกเบี้ย 5% ต่อปี");
      for (let i = 1; i < investColumnsCount; i++) {
        headerRow1.push(""); // Empty cells for merge
      }
    }
    headerRow1.push("");
    
    worksheetData.push(headerRow1);

    // Row 2: Second layer header (detail headers)
    const headerRow2 = [""];
    headerRow2.push("ค่าใช้จ่ายเพื่อการศึกษาต่อปี");
    headerRow2.push("ค่าใช้จ่ายสะสมเพื่อการศึกษาต่อปี");
    
    // Add dynamic investment column headers
    if (firstItem?.invest_keys) {
      firstItem.invest_keys.forEach((key, index) => {
        const group_label = _.groupBy(
          localEducationData?.education_items,
          "education.key"
        );
        const group_label_keys = Object.keys(group_label);
        
        if (group_label_keys[index]) {
          const label = key_education_rank?.[group_label_keys[index]] || key;
          headerRow2.push(label);
        }
      });
    }
    
    headerRow2.push("ออมเงิน / ลงทุน เพื่อเป้าหมาย");
    
    worksheetData.push(headerRow2);

    // Add data rows
    localEducationData?.education_items?.forEach((row) => {
      const dataRow = [row.education?.label || ""];
      dataRow.push(row?.sum_under_education || 0);
      dataRow.push(row?.sum_under_education_accumulate || 0);

      // Add dynamic investment columns
      row?.invest_keys?.forEach((key) => {
        const value = row.invest_dynamic_education_item_5[`${key}_5`];
        dataRow.push(value || 0);
      });

      dataRow.push(row?.sum_invest || 0);
      worksheetData.push(dataRow);
    });

    // Add summary row
    const summaryRow = ["รวม"];
    summaryRow.push(localEducationData?.sum_total_education || 0);
    summaryRow.push("");
    
    // Add empty cells for dynamic columns
    firstItem?.invest_keys?.forEach(() => {
      summaryRow.push("");
    });
    
    summaryRow.push(localEducationData?.sum_total_invest || 0);
    worksheetData.push(summaryRow);

    // Generate filename
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear() + 543;
    const dateStr = `${day}${month}${year}`;
    const username = `${userData?.firstname || ""} ${userData?.lastname || ""}`.trim() || "User";
    const fileName = `${username}-education-${dateStr}`;

    // Create workbook and worksheet using XLSX directly
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Define merge ranges for header
    const merges = [];
    
    // Merge first header row cells
    // Merge "ค่าใช้จ่ายเพื่อการศึกษาและค่าใช้จ่ายในชีวิตประจำวัน" (cols B-C)
    merges.push({ s: { r: 0, c: 1 }, e: { r: 0, c: 2 } });
    
    // Merge "ออมเงิน / ลงทุน ดอกเบี้ย 5% ต่อปี" across invest columns
    if (investColumnsCount > 0) {
      merges.push({ 
        s: { r: 0, c: 3 }, 
        e: { r: 0, c: 3 + investColumnsCount - 1 } 
      });
    }
    
    ws['!merges'] = merges;

    // Set column widths
    const colWidths = [
      { wch: 20 }, // Level column
      { wch: 30 }, // Annual expenses
      { wch: 30 }, // Accumulated expenses
    ];
    
    // Add widths for investment columns
    for (let i = 0; i < investColumnsCount; i++) {
      colWidths.push({ wch: 25 });
    }
    colWidths.push({ wch: 30 }); // Total invest column
    
    ws['!cols'] = colWidths;

    // Create workbook and add worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ค่าใช้จ่ายการศึกษา");

    // Write file
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };

  return (
    <div>
      <div className=" mt-2">
        <LineChartEduExpensesMain
          dataExpenses={dataExpenses}
          labels={dataLabels}
        />
      </div>
      {/* ตารางสรุปค่าใช้จ่ายเพื่อการศึกษา */}
      <div
        style={{
          height: `${checkHeight(height)}px`,
        }}
        className="px-6 rounded-[20px] overflow-auto  mt-[14px]"
      >
        <div className=" space-y-[10px]  ">
          <MTButton
            onClick={exportToExcel}
            flexnone="flex-none"
            width="w-fit text-xs"
            text="Excel"
            iconleft
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_6711_51468)">
                  <path
                    d="M14.1733 8.54535H11.6287V7.27304H14.1733V8.54535ZM14.1733 9.27235H11.6287V10.5447H14.1733V9.27235ZM14.1733 3.27441H11.6287V4.5467H14.1733V3.27441ZM14.1733 5.27373H11.6287V6.54601H14.1733V5.27373ZM14.1733 11.2717H11.6287V12.544H14.1733V11.2717ZM15.9309 13.7799C15.8582 14.158 15.4038 14.167 15.1003 14.1798H9.44769V15.8156H8.31897L0 14.3615V1.64045L8.36806 0.18457H9.44769V1.63316H14.9058C15.213 1.64588 15.5511 1.62407 15.8182 1.80763C16.0054 2.07663 15.9873 2.41835 16 2.72732L15.9927 12.1895C15.9837 12.7184 16.0418 13.2583 15.9309 13.7799ZM6.66497 10.9282C6.16334 9.91035 5.65263 8.89976 5.15278 7.88191C5.64716 6.89135 6.13428 5.89716 6.61956 4.90295C6.20697 4.92295 5.79438 4.94838 5.38363 4.97748C5.07644 5.72448 4.71838 6.45151 4.47847 7.22398C4.25491 6.49513 3.95866 5.79354 3.68784 5.08288C3.28797 5.10469 2.88809 5.12832 2.48825 5.15195C2.90991 6.08257 3.35887 7.00038 3.76781 7.93645C3.28616 8.84523 2.83541 9.76676 2.36828 10.681C2.76631 10.6973 3.16438 10.7137 3.56241 10.7191C3.84597 9.99576 4.19856 9.29963 4.44575 8.5617C4.6675 9.35416 5.04372 10.0866 5.35272 10.8445C5.79075 10.8755 6.22694 10.9027 6.66497 10.9282ZM15.1331 2.49638H9.44769V3.27441H10.9017V4.5467H9.44769V5.27373H10.9017V6.54601H9.44769V7.27304H10.9017V8.54535H9.44769V9.27235H10.9017V10.5447H9.44769V11.2717H10.9017V12.544H9.44769V13.3845H15.1331V2.49638Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_6711_51468">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            }
          />
          <ViewTableExpenses localEducationData={localEducationData} />
        </div>
      </div>
    </div>
  );
};

export default ViewExpensesEDU;
